import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ENV_CONFIG } from 'config/env';
import {
  useGetCubePendingDependencySync,
  useRetryCubePendingDependencySync,
} from 'services/v1/SystemTenant/AROCube/CubeRestructureService';
import {
  CubePendingDependencySyncState,
  ResponseCubePendingDependencySync,
} from 'types/api/SystemTenant/AROCube/CubeRestructureTypes';
import { dayParseUTCFormat } from 'utils/DateUtils';

const stateLabels: Record<CubePendingDependencySyncState, string> = {
  ACTIVE: 'Queued or running',
  STALE: 'Stalled',
  FAILED: 'Failed',
  OBSOLETE: 'No longer in the cube, removed on retry',
};

const count = (value: number, noun: string) => `${value} ${noun}${value === 1 ? '' : 's'}`;

const target = ({
  layerId,
  layerName,
  memberId,
  memberName,
}: Pick<ResponseCubePendingDependencySync['rows'][number], 'layerId' | 'layerName' | 'memberId' | 'memberName'>) =>
  `${layerName || layerId} · ${memberName || memberId}`;

/**
 * Lists the dependency sync rows that block restructuring a cube and retries them.
 * Loads only after the data check or restructure fails, and keeps the last retry result until the cube changes.
 */
export function CubePendingDependencySync({
  cubeId,
  failed,
  onResolved,
}: {
  cubeId: string;
  failed: boolean;
  onResolved: () => void;
}) {
  const pending = useGetCubePendingDependencySync(failed ? cubeId : undefined);
  const retry = useRetryCubePendingDependencySync();
  const rows = pending.data?.rows || [];
  const summary = retry.data?.retry;
  if (!(failed && (rows.length > 0 || pending.isError)) && !retry.data) return null;

  const handleRetry = async () => {
    try {
      const result = await retry.mutateAsync(cubeId);
      // The data check still lists the old blocker until it runs again.
      if (!result.rows.length) onResolved();
    } catch {
      // The error is shown from the mutation state.
    }
  };

  return (
    <Stack
      component='section'
      spacing={1.5}
      aria-labelledby='restructure-pending-sync-title'
      aria-live='polite'
      sx={{ borderTop: '1px solid #E3EBF6', pt: 2 }}
    >
      <Typography id='restructure-pending-sync-title' component='h3' variant='input-label' fontWeight='bold'>
        Pending dependency sync
      </Typography>
      {pending.isError && (
        <Alert severity='error'>
          {pending.error?.response?.data?.message || 'Unable to load the pending dependency sync. Please try again.'}
        </Alert>
      )}
      {retry.isError && (
        <Alert severity='error'>
          {retry.error?.response?.data?.message || 'Unable to retry the dependency sync. Please try again.'}
        </Alert>
      )}
      {summary && (
        <Alert severity={summary.failures.length ? 'error' : 'success'}>
          <AlertTitle sx={{ fontWeight: 600 }}>
            {summary.failures.length ? 'Dependency sync retry needs attention' : 'Dependency sync retried'}
          </AlertTitle>
          <Typography variant='body2'>
            {count(summary.synced, 'member')} synced, {count(summary.discarded, 'obsolete row')} removed,{' '}
            {summary.failures.length} failed.
          </Typography>
          {summary.failures.length > 0 && (
            <Stack component='ul' spacing={0.5} sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {summary.failures.map((failure) => (
                <Typography component='li' variant='body2' key={`${failure.layerId}:${failure.memberId}`}>
                  {target(failure)}: {failure.error}
                </Typography>
              ))}
            </Stack>
          )}
        </Alert>
      )}
      {rows.length > 0 && (
        <>
          <Typography variant='body2' color='text.secondary'>
            {pending.data?.message} Data is read from {pending.data?.tenantName}.
          </Typography>
          <Stack
            component='ul'
            spacing={0.75}
            aria-label='Pending dependency sync rows'
            sx={{ m: 0, pl: 2.5, maxHeight: 200, overflowY: 'auto' }}
          >
            {rows.map((row) => (
              <Typography component='li' variant='body2' key={row.id}>
                {target(row)} · {row.attributeName || row.attributeId || 'All attributes'}{' '}
                <Typography component='span' variant='body2' color='text.secondary'>
                  — {stateLabels[row.state] || row.state}
                  {row.updatedAt && `, updated ${dayParseUTCFormat(row.updatedAt, ENV_CONFIG.config.DATE_TIME_FORMAT)}`}
                </Typography>
              </Typography>
            ))}
          </Stack>
        </>
      )}
      {(rows.length > 0 || pending.isError) && (
        <Stack direction='row' justifyContent='flex-end' flexWrap='wrap' gap={1.5}>
          <Button
            variant='secondary-table-panel'
            disabled={pending.isFetching || retry.isLoading}
            onClick={() => void pending.refetch()}
          >
            Refresh
          </Button>
          <Button
            variant='main-table-panel'
            disabled={!pending.data?.canRetry || pending.isFetching || retry.isLoading}
            onClick={() => void handleRetry()}
          >
            {retry.isLoading ? 'Retrying...' : 'Retry Dependency Sync'}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
